import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { duplicateDatabaseForCompany } from '@/lib/api/company/companyDatabases'
import { useQueryClient } from '@tanstack/react-query'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'

interface DuplicateDatabaseModalProps {
  isOpen: boolean
  close: () => void
  selectedCompanyIds: string[]
  companyId: string
  onSuccess?: () => void
}

const DuplicateDatabaseModal: React.FC<DuplicateDatabaseModalProps> = ({
  isOpen,
  close,
  selectedCompanyIds,
  companyId,
  onSuccess,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)
  const queryClient = useQueryClient()
  const { data: companies = [] } = useQueryCompanies({
    includeDeleted: false,
  })
  const [error, setError] = React.useState<string | null>(null)
  const [successCount, setSuccessCount] = React.useState(0)

  const selectedCompanies = React.useMemo(() => {
    return companies.filter((company) =>
      selectedCompanyIds.includes(company.id),
    )
  }, [companies, selectedCompanyIds])

  const handleConfirm = async () => {
    setError(null)
    setSuccessCount(0)
    const stopLoading = startLoading()

    try {
      let success = 0
      const errors: string[] = []
      const unauthorizedCompanies: string[] = []
      let hasUnauthorizedError = false

      // Process companies one by one
      // If any authorization error occurs, stop immediately and cancel the entire request
      for (const company of selectedCompanies) {
        try {
          await duplicateDatabaseForCompany({
            companyId: company.id,
          })
          success++
          setSuccessCount(success)
        } catch (err: any) {
          const status = err?.response?.status
          const errorCode = err?.response?.data?.code
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            t('administrationPanel-companies.duplicate-database-error')

          // Check if it's an authorization/access error
          const isAuthError =
            status === 401 ||
            status === 403 ||
            errorCode === 'COMPANY_NOT_FOUND' ||
            errorCode === 'NOT_AUTHORIZED' ||
            errorCode === 'UNAUTHORIZED' ||
            errorMessage.toLowerCase().includes('access') ||
            errorMessage.toLowerCase().includes('permission') ||
            errorMessage.toLowerCase().includes('authorized') ||
            errorMessage.toLowerCase().includes('brak dostępu') ||
            errorMessage.toLowerCase().includes('nie ma uprawnień')

          if (isAuthError) {
            // Authorization error detected - cancel entire request immediately
            hasUnauthorizedError = true
            const companyName = company.companyName || company.uniqueIdentifier || company.id
            unauthorizedCompanies.push(companyName)

            // Stop processing immediately
            break
          } else {
            // Handle specific database duplication errors
            let displayMessage = errorMessage
            if (errorCode === 'DATABASE_DUPLICATION_FAILED') {
              displayMessage = t('administrationPanel-companies.duplicate-database-error-failed').replace('{0}', errorMessage)
            } else if (errorCode === 'BACKUP_LOCATION_NOT_SET') {
              displayMessage = t('administrationPanel-companies.duplicate-database-error-backup-location-not-set').replace('{0}', errorMessage)
            } else if (errorCode === 'DATABASE_EXISTS') {
              displayMessage = t('administrationPanel-companies.duplicate-database-error-database-exists')
            } else if (errorMessage.toLowerCase().includes('backup') || errorMessage.toLowerCase().includes('kopia zapasowa')) {
              displayMessage = t('administrationPanel-companies.duplicate-database-error-backup-failed').replace('{0}', errorMessage)
            }

            // Other errors - collect for reporting
            errors.push(`${company.companyName || company.id}: ${displayMessage}`)
          }
        }
      }

      // If any unauthorized access detected, cancel entire request
      if (hasUnauthorizedError) {
        const unauthorizedList = unauthorizedCompanies.join('\n')
        setError(
          `${t('databases.no-access-to-companies')}:\n${unauthorizedList}\n\n${t('databases.no-duplicates-created')}`,
        )
        return
      }

      // Show errors if any (but not authorization errors - those were handled above)
      if (errors.length > 0) {
        setError(errors.join('\n'))
      }

      if (success > 0) {
        // Refresh company list and company databases
        await queryClient.invalidateQueries({ queryKey: ['config.companies'] })
        await queryClient.invalidateQueries({ queryKey: ['company-databases'] })
        if (onSuccess) {
          onSuccess()
        }
        // Close modal after a short delay to show success
        setTimeout(() => {
          close()
        }, 1500)
      } else if (errors.length > 0) {
        // Keep modal open if all failed - don't close, just stop loading
      } else {
        close()
      }
    } catch (err: any) {
      setError(
          err?.response?.data?.message ||
          err?.message ||
          t('administrationPanel-companies.duplicate-database-error'),
      )
    } finally {
      stopLoading()
    }
  }

  if (!isOpen) return null

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[600px] tw-flex-col tw-rounded-lg tw-bg-white">
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        {/* Header */}
        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-text-xl tw-font-semibold tw-text-content-base">
              {t('administrationPanel-companies.duplicate-database')}
            </p>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={close}
            />
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6">
          <div className="tw-space-y-4">
            {/* Warning Message */}
            <div className="tw-rounded-lg tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-p-4">
              <p className="tw-text-sm tw-text-yellow-800">
                {t('administrationPanel-companies.duplicate-database-warning')}
              </p>
            </div>

            {/* Selected Companies List */}
            {selectedCompanies.length > 0 && (
              <div>
                <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-content-base">
                  {t('databases.selected-companies')} ({selectedCompanies.length}):
                </p>
                <div className="tw-max-h-48 tw-overflow-y-auto tw-rounded tw-border tw-border-base-300">
                  <ul className="tw-divide-y tw-divide-base-300">
                    {selectedCompanies.map((company) => (
                      <li
                        key={company.id}
                        className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base"
                      >
                        {company.companyName || company.uniqueIdentifier || company.id}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Success Message */}
            {successCount > 0 && (
              <div className="tw-rounded-lg tw-bg-green-50 tw-border tw-border-green-200 tw-p-4">
                <p className="tw-text-sm tw-text-green-800">
                  {t('databases.duplicates-created-count').replace('{success}', String(successCount)).replace('{total}', String(selectedCompanies.length))}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="tw-rounded-lg tw-bg-red-50 tw-border tw-border-red-200 tw-p-4">
                <p className="tw-text-sm tw-text-red-800 tw-whitespace-pre-line">
                  {error}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              type="button"
              onClick={close}
              disabled={loading}
              className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100 disabled:tw-opacity-50"
            >
              {t('administrationPanel-companies.duplicate-database-cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || selectedCompanies.length === 0}
              className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
            >
              {t('administrationPanel-companies.duplicate-database-confirm')}
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default DuplicateDatabaseModal
